import { useState, useCallback } from 'react';
import { LexAIService } from '../services/api';

interface SubTask {
  id: string;
  title: string;
  completed: boolean;
}

interface UseTaskSubtasksProps {
  taskId: string;
}

interface UseTaskSubtasksReturn {
  subtasks: SubTask[];
  isLoadingSubtasks: boolean;
  loadSubtasks: () => Promise<void>;
  addSubtask: (title: string) => Promise<void>;
  toggleSubtask: (subtaskId: string, completed: boolean) => Promise<void>;
  deleteSubtask: (subtaskId: string) => Promise<void>;
}

export const useTaskSubtasks = ({ taskId }: UseTaskSubtasksProps): UseTaskSubtasksReturn => {
  const [subtasks, setSubtasks] = useState<SubTask[]>([]);
  const [isLoadingSubtasks, setIsLoadingSubtasks] = useState(false);

  const loadSubtasks = useCallback(async () => {
    try {
      setIsLoadingSubtasks(true);
      const taskSubtasks = await LexAIService.getTaskSubtasks(taskId);
      setSubtasks(taskSubtasks);
    } catch (error) {
      // For now, if we can't load subtasks, show empty array instead of fake data
      setSubtasks([]);
    } finally {
      setIsLoadingSubtasks(false);
    }
  }, [taskId]);

  const addSubtask = useCallback(async (title: string) => {
    if (!title.trim()) return;

    try {
      const newSubtask = await LexAIService.createTaskSubtask(taskId, title.trim());
      if (newSubtask) {
        setSubtasks(prev => [...prev, newSubtask]);
      }
    } catch (error) {
      // Failed to create checklist item
    }
  }, [taskId]);

  const toggleSubtask = useCallback(async (subtaskId: string, completed: boolean) => {
    try {
      const updatedSubtask = await LexAIService.updateTaskSubtask(taskId, subtaskId, {
        completed: completed
      });

      if (updatedSubtask) {
        setSubtasks(prev => prev.map(st =>
          st.id === subtaskId ? updatedSubtask : st
        ));
      }
    } catch (error) {
      // Fallback to local update
      setSubtasks(prev => prev.map(st =>
        st.id === subtaskId ? { ...st, completed: completed } : st
      ));
    }
  }, [taskId]);

  const deleteSubtask = useCallback(async (subtaskId: string) => {
    try {
      const success = await LexAIService.deleteTaskSubtask(taskId, subtaskId);
      if (success) {
        setSubtasks(prev => prev.filter(st => st.id !== subtaskId));
      }
    } catch (error) {
      // Fallback to local removal
      setSubtasks(prev => prev.filter(st => st.id !== subtaskId));
    }
  }, [taskId]);

  return {
    subtasks,
    isLoadingSubtasks,
    loadSubtasks,
    addSubtask,
    toggleSubtask,
    deleteSubtask
  };
};
